import { configureApi } from '@mehir/api/config';

const FALLBACK_API_URL = 'https://api-test.mehir.app/';

const resolveApiUrl = (): string => {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return configured;

  // A production bundle built without VITE_API_URL used to silently talk to the
  // test API and look perfectly healthy. Refuse to start instead. Dev keeps the
  // fallback so a fresh checkout without a .env still runs.
  if (import.meta.env.PROD) {
    throw new Error(
      'VITE_API_URL is not set. Copy apps/backoffice/.env.example to .env and set the API base URL before building.'
    );
  }

  console.warn(`VITE_API_URL is not set — falling back to ${FALLBACK_API_URL}`);
  return FALLBACK_API_URL;
};

export const BACKOFFICE_API_URL = resolveApiUrl();

export const TOKEN_KEY = 'backoffice-token';
export const REFRESH_TOKEN_KEY = 'backoffice-refresh-token';

const REFRESH_URL = `${BACKOFFICE_API_URL.replace(/\/$/, '')}/backofficeservice/v1/Authentication/auth/refresh-token`;

/** Refresh this many ms before the access token actually expires. */
const REFRESH_SKEW_MS = 60_000;

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string | null;
}

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode / quota) — requests just go unauthenticated */
  }
};

const drop = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

/**
 * Expiry of a JWT in ms, read from the `exp` claim. We deliberately do not rely
 * on the login response's `expiresIn`: the claim is authoritative and is also
 * available for tokens restored from a previous session.
 */
export const getTokenExpiry = (token: string | null | undefined): number | null => {
  if (!token) return null;
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: number }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};

let onSessionExpired: (() => void) | null = null;

/**
 * Registered by the app so the store can drop its auth state when a refresh
 * fails. Kept as a callback to avoid importing the store from this module.
 */
export const setSessionExpiredHandler = (handler: (() => void) | null) => {
  onSessionExpired = handler;
};

export const tokenStore = {
  getAccessToken: () => read(TOKEN_KEY),
  getRefreshToken: () => read(REFRESH_TOKEN_KEY),

  save: ({ accessToken, refreshToken }: AuthTokens) => {
    write(TOKEN_KEY, accessToken);
    if (refreshToken) write(REFRESH_TOKEN_KEY, refreshToken);
  },

  clear: () => {
    drop(TOKEN_KEY);
    drop(REFRESH_TOKEN_KEY);
  },
};

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchanges the refresh token for a new access token. Concurrent callers share
 * a single request so a page full of queries cannot fire N refreshes at once.
 */
const refreshAccessToken = (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = tokenStore.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const response = await fetch(REFRESH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) throw new Error(`Refresh failed with ${response.status}`);

      const data = (await response.json()) as { accessToken?: string; refreshToken?: string };
      if (!data.accessToken) throw new Error('Refresh response carried no access token');

      tokenStore.save({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      return data.accessToken;
    } catch {
      tokenStore.clear();
      onSessionExpired?.();
      return null;
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

/**
 * Access token for the next request, refreshed first when it is about to
 * expire. Returns null when there is no usable session.
 */
export const getFreshAccessToken = async (): Promise<string | null> => {
  const token = tokenStore.getAccessToken();
  if (!token) return null;

  const expiresAt = getTokenExpiry(token);
  // No readable expiry → use the token as-is; a 401 is handled by the store's
  // error middleware.
  if (expiresAt === null) return token;
  if (Date.now() < expiresAt - REFRESH_SKEW_MS) return token;

  return refreshAccessToken();
};

// Initialize libs/api with backoffice-specific base URL and auth headers
configureApi(BACKOFFICE_API_URL, async (headers) => {
  const token = await getFreshAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
});
